import React from 'react';
import type { OrgNavigationFilters } from './OrgNavigationContent';
import { matchPath, useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import {
    createDefaultOrgNavigationFilters,
    getStoredOrgNavigationFilters,
    hasStoredOrgNavigationFilters,
    setStoredOrgNavigationFilters,
    recomputeRelativeDateRangeIfNeeded,
} from './orgNavigationFiltersStorage';
import {
    areSerializableFiltersEqual,
    buildOrgNavigationSearchParams,
    ORG_OVERVIEW_QUERY_PARAMS,
    ORG_OVERVIEW_PATH,
    parseOrgNavigationFiltersFromSearch,
} from './orgNavigationUrlParams';
import { hasAnyParamInSearch } from '@/helpers/urlParamHelpers';
import { useSelector } from 'react-redux';
import { getIdFromOrgId } from '@/helpers';
import { OrgRouteComponentMatchParams } from '@/typescriptHelpers';
import routes from '@/routes';
import { RootState } from '@/reducers/reducers';

interface OrgNavigationFiltersContextValue {
    filters: OrgNavigationFilters;
    hasStoredFilters: boolean;
    setFilters: React.Dispatch<React.SetStateAction<OrgNavigationFilters>>;
    setShouldSyncUrlFilters: React.Dispatch<React.SetStateAction<boolean>>;
}

const OrgNavigationFiltersContext = React.createContext<OrgNavigationFiltersContextValue | null>(null);

export const OrgNavigationFiltersProvider: React.FC<{
    children: React.ReactNode;
}> = ({ children }) => {
    const history = useHistory();
    const location = useLocation();
    const isOrgOverviewRoute = !!matchPath(location.pathname, {
        path: ORG_OVERVIEW_PATH,
        exact: true,
        strict: false,
    });

    const orgRouteMatch = useRouteMatch<OrgRouteComponentMatchParams>(routes.org);
    const orgIdParam = orgRouteMatch?.params.orgIdParam;
    const orgId = useSelector((state: RootState) => {
        if (orgIdParam) {
            return getIdFromOrgId(orgIdParam, state) || '';
        }

        return '';
    });
    const [shouldSyncUrlFilters, setShouldSyncUrlFilters] = React.useState(false);
    const shouldUseUrlFiltersOnInitialLoad =
        isOrgOverviewRoute && hasAnyParamInSearch(location.search, Object.values(ORG_OVERVIEW_QUERY_PARAMS));
    const initialUrlFilters = React.useMemo(
        () => (shouldUseUrlFiltersOnInitialLoad ? parseOrgNavigationFiltersFromSearch(location.search) : null),
        [location.search, shouldUseUrlFiltersOnInitialLoad],
    );
    const [hasStoredFilters] = React.useState(() => hasStoredOrgNavigationFilters(orgId));
    const [filters, setFilters] = React.useState<OrgNavigationFilters>(() => {
        if (shouldUseUrlFiltersOnInitialLoad) {
            const urlFilters = parseOrgNavigationFiltersFromSearch(location.search);
            return recomputeRelativeDateRangeIfNeeded(urlFilters);
        }

        const storedFilters = getStoredOrgNavigationFilters(orgId) ?? createDefaultOrgNavigationFilters();
        return recomputeRelativeDateRangeIfNeeded(storedFilters);
    });
    const initialUrlFiltersRef = React.useRef(initialUrlFilters);

    React.useEffect(() => {
        if (!shouldSyncUrlFilters) {
            return;
        }

        if (initialUrlFiltersRef.current) {
            if (areSerializableFiltersEqual(filters, initialUrlFiltersRef.current)) {
                return;
            }

            initialUrlFiltersRef.current = null;
        }

        setStoredOrgNavigationFilters(orgId, filters);
    }, [filters, shouldSyncUrlFilters]);

    React.useEffect(() => {
        if (!isOrgOverviewRoute || !shouldSyncUrlFilters) {
            return;
        }

        const nextSearchParams = buildOrgNavigationSearchParams(filters, location.search);
        const nextSearch = nextSearchParams.toString();
        const currentSearch = location.search.replace(/^\?/, '');

        if (nextSearch === currentSearch) {
            return;
        }

        history.replace({
            ...location,
            search: nextSearch,
        });
    }, [filters, history, isOrgOverviewRoute, location, shouldSyncUrlFilters]);

    return (
        <OrgNavigationFiltersContext.Provider
            value={{
                filters,
                hasStoredFilters,
                setFilters,
                setShouldSyncUrlFilters,
            }}
        >
            {children}
        </OrgNavigationFiltersContext.Provider>
    );
};

export const useOrgNavigationFilters = (): OrgNavigationFiltersContextValue => {
    const context = React.useContext(OrgNavigationFiltersContext);

    if (!context) {
        throw new Error('useOrgNavigationFilters must be used within OrgNavigationFiltersProvider');
    }

    return context;
};
